import type {
  StudioActionFlowReadinessEvidence,
  StudioOntologyRetrievalTrace,
} from '@/services/block-api-service'
import {
  STUDIO_EXPECTED_EMBEDDING_DIMENSION,
  STUDIO_EXPECTED_EMBEDDING_MODEL,
  validateStudioOntologyRetrievalTrace,
} from './ontology-retrieval-evidence'

export const CHAT_ONTOLOGY_REALTIME_TARGET_MS = 15_000
export const CHAT_ONTOLOGY_INTERACTIVE_TARGET_MS = 60_000

export type StudioChatOntologyGenerationQualityStatus =
  | 'passed'
  | 'warning'
  | 'failed'

export type StudioChatOntologySpeedClass =
  | 'realtime'
  | 'interactive'
  | 'slow'
  | 'unknown'

export interface StudioChatOntologyGenerationQualityInput {
  prompt?: string
  planningSource?: string
  /**
   * Set only by the MCP boundary after it has read and validated the final
   * authority persisted on the exact destination project row.
   */
  verifiedPersistedApplicationAuthorityMode?:
    | 'provided-spec'
    | 'locked-exact-spec'
  fallbackReason?: string
  decomposeElapsedMs?: number
  decomposeTotalMs?: number
  decomposeUpstreamMs?: number
  ontologyTrace?: StudioOntologyRetrievalTrace | Record<string, unknown> | null
  expectedBlockNames?: string[]
  generatedBlockNames?: string[]
  persistedBlockNames?: string[]
  actionFlowCompilerValid?: boolean
  actionFlowRuntimeValid?: boolean
  actionFlowReadiness?:
    | Pick<StudioActionFlowReadinessEvidence, 'status' | 'required'>
    | Record<string, unknown>
    | null
}

export interface StudioChatOntologyGenerationQuality {
  schemaVersion: 'studio-chat-ontology-generation-quality.v1'
  status: StudioChatOntologyGenerationQualityStatus
  generatedAt: string
  summary: {
    score: number
    speedScore: number
    ontologyUtilizationScore: number
    generationAccuracyScore: number
    issueCount: number
  }
  speed: {
    class: StudioChatOntologySpeedClass
    latencyMs?: number
    targetMs: number
    realtimeTargetMs: number
    interactiveTargetMs: number
    realtimeTargetMet: boolean
    interactiveTargetMet: boolean
    budgetMarginMs?: number
    upstreamMs?: number
  }
  ontology: {
    source?: string
    mode?: string
    traceValid: boolean
    embeddingUsed: boolean
    embeddingModel?: string
    embeddingDimension?: number
    structuralCandidateCount: number
    vectorCandidateCount: number
    admittedCandidateCount: number
    selectedSubjectCount: number
    readbackSubjectCount: number
    readbackPassed: boolean
    fallbackUsed: boolean
    utilizationScore: number
  }
  accuracy: {
    planningSource?: string
    expectedBlockCount: number
    generatedBlockCount: number
    persistedBlockCount: number
    matchedBlockCount: number
    persistedMatchedBlockCount: number
    generatedBlockCoverage: number
    persistedBlockCoverage: number
    actionFlowCompilerValid?: boolean
    actionFlowRuntimeValid?: boolean
    actionFlowReadinessStatus?: string
    score: number
  }
  issues: Array<{
    code: string
    severity: 'error' | 'warning'
    message: string
  }>
}

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined
}

function readStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is string =>
          typeof item === 'string' && item.trim().length > 0,
      )
    : []
}

function readTrace(
  value: StudioChatOntologyGenerationQualityInput['ontologyTrace'],
): Partial<StudioOntologyRetrievalTrace> {
  return readRecord(value) as Partial<StudioOntologyRetrievalTrace>
}

function speedScore(latencyMs: number | undefined): number {
  if (latencyMs === undefined) return 0
  if (latencyMs <= CHAT_ONTOLOGY_REALTIME_TARGET_MS) return 100
  if (latencyMs <= CHAT_ONTOLOGY_INTERACTIVE_TARGET_MS) {
    const over = latencyMs - CHAT_ONTOLOGY_REALTIME_TARGET_MS
    const range =
      CHAT_ONTOLOGY_INTERACTIVE_TARGET_MS - CHAT_ONTOLOGY_REALTIME_TARGET_MS
    return Math.round(85 - (over / range) * 15)
  }
  if (latencyMs <= CHAT_ONTOLOGY_INTERACTIVE_TARGET_MS * 2) return 45
  return 15
}

function speedClass(
  latencyMs: number | undefined,
): StudioChatOntologySpeedClass {
  if (latencyMs === undefined) return 'unknown'
  if (latencyMs <= CHAT_ONTOLOGY_REALTIME_TARGET_MS) return 'realtime'
  if (latencyMs <= CHAT_ONTOLOGY_INTERACTIVE_TARGET_MS) return 'interactive'
  return 'slow'
}

function matchedCoverage(
  expected: string[],
  generated: string[],
): {
  matchedBlockCount: number
  generatedBlockCoverage: number
} {
  if (expected.length === 0) {
    return {
      matchedBlockCount: generated.length > 0 ? generated.length : 0,
      generatedBlockCoverage: generated.length > 0 ? 1 : 0,
    }
  }
  const generatedSet = new Set(generated)
  const matchedBlockCount = expected.filter((name) =>
    generatedSet.has(name),
  ).length
  return {
    matchedBlockCount,
    generatedBlockCoverage: matchedBlockCount / expected.length,
  }
}

export function evaluateChatOntologyGenerationQuality(
  input: StudioChatOntologyGenerationQualityInput,
): StudioChatOntologyGenerationQuality {
  const issues: StudioChatOntologyGenerationQuality['issues'] = []
  const addIssue = (
    code: string,
    message: string,
    severity: 'error' | 'warning' = 'error',
  ) => issues.push({ code, severity, message })

  const trace = readTrace(input.ontologyTrace)
  const validation = validateStudioOntologyRetrievalTrace(
    Object.keys(trace).length > 0
      ? (trace as StudioOntologyRetrievalTrace)
      : undefined,
  )
  for (const issue of validation.issues) {
    addIssue(issue.code, issue.message, issue.severity)
  }

  const retrievalMode = readString(trace.mode)
  const authoritativeRetrievalModes = new Set([
    'facet-sparql',
    'lexical-sparql',
    'ontology-fast-path',
    'vector-rerank',
    'llm-rerank',
  ])
  if (!retrievalMode || !authoritativeRetrievalModes.has(retrievalMode)) {
    addIssue(
      'non-authoritative-ontology-retrieval',
      'Chat app generation requires an admitted ontology retrieval result; structural or unavailable fallback evidence cannot authorize generation.',
    )
  }

  const hasVerifiedPersistedNoPlannerAuthority =
    input.verifiedPersistedApplicationAuthorityMode === 'provided-spec' ||
    input.verifiedPersistedApplicationAuthorityMode === 'locked-exact-spec'
  if (
    (!hasVerifiedPersistedNoPlannerAuthority &&
      input.planningSource !== 'cogo-decompose-api') ||
    readBoolean(trace.fallbackUsed) === true
  ) {
    addIssue(
      'fallback-generation-not-admitted',
      'Local or fallback planning is diagnostic evidence only and cannot create an application.',
    )
  }

  const latencyMs = input.decomposeTotalMs ?? input.decomposeElapsedMs
  if (latencyMs === undefined) {
    addIssue(
      'missing-chat-generation-latency',
      'Chat generation canary must record live planning latency.',
      'warning',
    )
  } else if (latencyMs > CHAT_ONTOLOGY_INTERACTIVE_TARGET_MS) {
    addIssue(
      'chat-generation-slow',
      `Live ontology planning took ${latencyMs}ms; target is <= ${CHAT_ONTOLOGY_INTERACTIVE_TARGET_MS}ms.`,
      'warning',
    )
  }

  const embeddingUsed = readBoolean(trace.embeddingUsed) === true
  if (embeddingUsed) {
    if (trace.embeddingModel !== STUDIO_EXPECTED_EMBEDDING_MODEL) {
      addIssue(
        'embedding-model-mismatch',
        `Expected ${STUDIO_EXPECTED_EMBEDDING_MODEL} embedding evidence.`,
      )
    }
    if (trace.embeddingDimension !== STUDIO_EXPECTED_EMBEDDING_DIMENSION) {
      addIssue(
        'embedding-dimension-mismatch',
        `Expected ${STUDIO_EXPECTED_EMBEDDING_DIMENSION}-dimension embedding evidence.`,
      )
    }
  }

  if (input.fallbackReason) {
    addIssue(
      'live-planner-fallback-used',
      `Live planner fallback was used: ${input.fallbackReason}`,
    )
  }

  const expectedBlockNames = [...new Set(input.expectedBlockNames ?? [])]
  const generatedBlockNames = [...new Set(input.generatedBlockNames ?? [])]
  const persistedBlockNames = [
    ...new Set(input.persistedBlockNames ?? generatedBlockNames),
  ]
  const { matchedBlockCount, generatedBlockCoverage } = matchedCoverage(
    expectedBlockNames,
    generatedBlockNames,
  )
  const {
    matchedBlockCount: persistedMatchedBlockCount,
    generatedBlockCoverage: persistedBlockCoverage,
  } = matchedCoverage(expectedBlockNames, persistedBlockNames)
  if (generatedBlockCoverage < 1) {
    addIssue(
      'generated-block-coverage-incomplete',
      'Generated project did not persist every expected block from the live plan.',
    )
  }
  if (persistedBlockCoverage < 1) {
    addIssue(
      'persisted-block-coverage-incomplete',
      'Project full readback did not return every expected generated block from studio_blocks.',
    )
  }

  const readiness = readRecord(input.actionFlowReadiness)
  const actionFlowReadinessStatus = readString(readiness.status)
  const actionFlowRequired = readBoolean(readiness.required)
  if (input.actionFlowCompilerValid === false) {
    addIssue(
      'actionflow-compiler-proof-invalid',
      'ActionFlow compiler proof was invalid for a chat-generated app.',
    )
  }
  if (input.actionFlowRuntimeValid === false) {
    addIssue(
      'actionflow-runtime-proof-invalid',
      'ActionFlow runtime simulation proof was invalid for a chat-generated app.',
    )
  }
  if (
    actionFlowRequired === true &&
    actionFlowReadinessStatus &&
    actionFlowReadinessStatus !== 'ready'
  ) {
    addIssue(
      'actionflow-readiness-not-ready',
      `ActionFlow readiness is ${actionFlowReadinessStatus}; expected ready.`,
    )
  }

  const structuralCandidateCount =
    readNumber(trace.structuralCandidateCount) ?? 0
  const vectorCandidateCount = readNumber(trace.vectorCandidateCount) ?? 0
  const admittedCandidateCount = readNumber(trace.admittedCandidateCount) ?? 0
  const selectedSubjectCount = readStringArray(
    trace.selectedOntologySubjects,
  ).length
  const readbackSubjectCount = readStringArray(trace.readbackSubjectIds).length
  const readbackPassed = readBoolean(trace.readbackPassed) === true
  const fallbackUsed = readBoolean(trace.fallbackUsed) === true
  const ontologyUtilizationScore = [
    validation.valid ? 25 : 0,
    readString(trace.source) && trace.source !== 'none' ? 10 : 0,
    structuralCandidateCount > 0 ? 10 : 0,
    admittedCandidateCount > 0 ? 15 : 0,
    readbackPassed && readbackSubjectCount > 0 ? 20 : 0,
    selectedSubjectCount > 0 ? 10 : 0,
    embeddingUsed ||
    vectorCandidateCount > 0 ||
    trace.mode === 'structural-only'
      ? 5
      : 0,
    !fallbackUsed && !input.fallbackReason ? 5 : 0,
  ].reduce((sum, value) => sum + value, 0)

  const accuracyScore = [
    input.planningSource === 'cogo-decompose-api' ||
    hasVerifiedPersistedNoPlannerAuthority
      ? 15
      : 0,
    Math.round(generatedBlockCoverage * 20),
    Math.round(persistedBlockCoverage * 15),
    input.actionFlowCompilerValid !== false ? 15 : 0,
    input.actionFlowRuntimeValid !== false ? 15 : 0,
    actionFlowReadinessStatus === 'ready' ||
    actionFlowReadinessStatus === 'not-required' ||
    !actionFlowReadinessStatus
      ? 10
      : 0,
    validation.valid ? 10 : 0,
  ].reduce((sum, value) => sum + value, 0)

  const speed = speedScore(latencyMs)
  const score = Math.round(
    speed * 0.25 + ontologyUtilizationScore * 0.4 + accuracyScore * 0.35,
  )
  const hasErrors = issues.some((issue) => issue.severity === 'error')
  const hasWarnings = issues.some((issue) => issue.severity === 'warning')
  const status: StudioChatOntologyGenerationQualityStatus = hasErrors
    ? 'failed'
    : hasWarnings
      ? 'warning'
      : 'passed'

  return {
    schemaVersion: 'studio-chat-ontology-generation-quality.v1',
    status,
    generatedAt: new Date().toISOString(),
    summary: {
      score,
      speedScore: speed,
      ontologyUtilizationScore,
      generationAccuracyScore: accuracyScore,
      issueCount: issues.length,
    },
    speed: {
      class: speedClass(latencyMs),
      ...(latencyMs !== undefined ? { latencyMs } : {}),
      targetMs: CHAT_ONTOLOGY_INTERACTIVE_TARGET_MS,
      realtimeTargetMs: CHAT_ONTOLOGY_REALTIME_TARGET_MS,
      interactiveTargetMs: CHAT_ONTOLOGY_INTERACTIVE_TARGET_MS,
      realtimeTargetMet:
        latencyMs !== undefined && latencyMs <= CHAT_ONTOLOGY_REALTIME_TARGET_MS,
      interactiveTargetMet:
        latencyMs !== undefined && latencyMs <= CHAT_ONTOLOGY_INTERACTIVE_TARGET_MS,
      ...(latencyMs !== undefined
        ? { budgetMarginMs: CHAT_ONTOLOGY_INTERACTIVE_TARGET_MS - latencyMs }
        : {}),
      ...(input.decomposeUpstreamMs !== undefined
        ? { upstreamMs: input.decomposeUpstreamMs }
        : {}),
    },
    ontology: {
      ...(readString(trace.source) ? { source: readString(trace.source) } : {}),
      ...(readString(trace.mode) ? { mode: readString(trace.mode) } : {}),
      traceValid: validation.valid,
      embeddingUsed,
      ...(readString(trace.embeddingModel)
        ? { embeddingModel: readString(trace.embeddingModel) }
        : {}),
      ...(readNumber(trace.embeddingDimension)
        ? { embeddingDimension: readNumber(trace.embeddingDimension) }
        : {}),
      structuralCandidateCount,
      vectorCandidateCount,
      admittedCandidateCount,
      selectedSubjectCount,
      readbackSubjectCount,
      readbackPassed,
      fallbackUsed,
      utilizationScore: ontologyUtilizationScore,
    },
    accuracy: {
      ...(input.planningSource ? { planningSource: input.planningSource } : {}),
      expectedBlockCount: expectedBlockNames.length,
      generatedBlockCount: generatedBlockNames.length,
      persistedBlockCount: persistedBlockNames.length,
      matchedBlockCount,
      persistedMatchedBlockCount,
      generatedBlockCoverage,
      persistedBlockCoverage,
      ...(input.actionFlowCompilerValid !== undefined
        ? { actionFlowCompilerValid: input.actionFlowCompilerValid }
        : {}),
      ...(input.actionFlowRuntimeValid !== undefined
        ? { actionFlowRuntimeValid: input.actionFlowRuntimeValid }
        : {}),
      ...(actionFlowReadinessStatus ? { actionFlowReadinessStatus } : {}),
      score: accuracyScore,
    },
    issues,
  }
}
